import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  ArrowLeft,
  Mic,
  MicOff,
  Volume2,
  Loader2,
  AlertCircle,
  PhoneCall,
  PhoneOff,
  Calendar,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const industryInfo = {
  hvac: { name: 'HVAC & Heating', icon: '🔥', color: 'from-orange-500 to-red-600' },
  plumbing: { name: 'Plumbing', icon: '💧', color: 'from-blue-500 to-cyan-600' },
  electrical: { name: 'Electrical', icon: '⚡', color: 'from-yellow-500 to-amber-600' },
  home_repair: { name: 'Home Repair', icon: '🔧', color: 'from-slate-500 to-zinc-600' },
  dental: { name: 'Dental & Medical', icon: '🦷', color: 'from-teal-500 to-emerald-600' },
  roofing: { name: 'Roofing', icon: '🏠', color: 'from-stone-500 to-slate-600' },
  landscaping: { name: 'Landscaping', icon: '🌲', color: 'from-green-500 to-emerald-600' },
  auto_repair: { name: 'Auto Repair', icon: '🚗', color: 'from-red-500 to-rose-600' },
  septic: { name: 'Septic Services', icon: '🚽', color: 'from-amber-600 to-yellow-700' },
  restaurant: { name: 'Restaurant', icon: '🍽️', color: 'from-amber-500 to-orange-600' },
  fishing_charter: { name: 'Fishing Charter', icon: '🎣', color: 'from-blue-600 to-indigo-600' },
  sightseeing: { name: 'Sightseeing Tours', icon: '🏔️', color: 'from-sky-500 to-blue-600' },
  gym: { name: 'Gym & Fitness', icon: '💪', color: 'from-purple-500 to-violet-600' }
};

export default function VoiceDemo() {
  const { industry } = useParams();
  const navigate = useNavigate();
  
  // Setup state - company name before starting demo
  const [companyName, setCompanyName] = useState('');
  const [demoStarted, setDemoStarted] = useState(false);
  
  // Demo state
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasPermission, setHasPermission] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [callActive, setCallActive] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [scheduledAppointment, setScheduledAppointment] = useState(null);
  
  // MediaRecorder refs for Whisper-based transcription
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  
  const info = industryInfo[industry] || industryInfo.hvac;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const requestMicPermission = async () => {
    try {
      console.log('Requesting microphone permission...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('Microphone access granted');
      streamRef.current = stream;
      setHasPermission(true);
      toast.success('Microphone access granted!');
      return true;
    } catch (error) {
      console.error('Microphone permission error:', error);
      setHasPermission(false);
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No microphone found. Please connect a microphone.');
      } else {
        toast.error(`Microphone error: ${error.message}`);
      }
      return false;
    }
  };

  const startCall = async () => {
    // Request permission if not already granted
    if (hasPermission === null || hasPermission === false) {
      const granted = await requestMicPermission();
      if (!granted) return;
    }
    
    setCallActive(true);
    setConversationHistory([]);
    setTranscript('');
    
    // AI greeting using the prospect's company name
    const greeting = `Hello! Thank you for calling ${companyName}. This is your AI assistant. How can I help you today?`;
    setAiResponse(greeting);
    
    // Use OpenAI TTS for natural voice greeting
    await speakText(greeting);
  };

  const endCall = () => {
    setCallActive(false);
    setIsListening(false);
    setIsSpeaking(false);
    setIsProcessing(false);
    setDemoStarted(false); // Return to setup screen
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    toast.info('Demo call ended');
  };

  const startListening = async () => {
    console.log('startListening called', { callActive, isProcessing, isSpeaking, hasPermission });
    
    if (!callActive) {
      console.log('Call not active, cannot listen');
      return;
    }
    
    if (isProcessing || isSpeaking) {
      console.log('Processing or speaking, cannot listen');
      return;
    }
    
    // Ensure we have mic permission and stream
    if (!streamRef.current || hasPermission !== true) {
      console.log('No mic stream, requesting...');
      const granted = await requestMicPermission();
      if (!granted) return;
    }
    
    try {
      // Stop any existing recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      
      audioChunksRef.current = [];
      
      // Check for stream validity
      if (!streamRef.current || !streamRef.current.active) {
        console.log('Stream not active, re-requesting mic permission');
        const granted = await requestMicPermission();
        if (!granted) {
          toast.error('Microphone access required');
          return;
        }
      }
      
      // Determine supported MIME type
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/wav'
      ];
      
      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }
      
      console.log('Selected MIME type:', selectedMimeType || 'default');
      
      // Create new MediaRecorder with fallback
      const recorderOptions = selectedMimeType ? { mimeType: selectedMimeType } : {};
      const mediaRecorder = new MediaRecorder(streamRef.current, recorderOptions);
      
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        console.log('Recording stopped, processing audio...');
        setIsListening(false);
        
        if (audioChunksRef.current.length === 0) {
          toast.info('No audio recorded. Please try again.');
          return;
        }
        
        // Create blob using the recorder's actual MIME type
        const actualMimeType = mediaRecorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        console.log('Audio blob size:', audioBlob.size, 'type:', actualMimeType);
        
        if (audioBlob.size < 1000) {
          toast.info('Recording too short. Please speak longer.');
          return;
        }
        
        setTranscript('Processing...');
        setIsProcessing(true);
        
        try {
          const formData = new FormData();
          // Determine file extension from MIME type
          const ext = actualMimeType.includes('webm') ? 'webm' : 
                      actualMimeType.includes('mp4') ? 'mp4' : 
                      actualMimeType.includes('ogg') ? 'ogg' : 'wav';
          formData.append('audio', audioBlob, `recording.${ext}`);
          
          const response = await axios.post(
            `${BACKEND_URL}/api/demo/speech-to-text`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          
          const text = response.data.text;
          console.log('Transcribed text:', text);
          
          if (text && text.trim()) {
            setTranscript(text);
            setIsProcessing(false);
            handleUserInput(text);
          } else {
            setTranscript('');
            setIsProcessing(false);
            toast.info('Could not understand. Please try again.');
          }
        } catch (error) {
          console.error('Transcription error:', error);
          setTranscript('');
          setIsProcessing(false);
          toast.error('Error processing speech. Please try again.');
        }
      };
      
      // Start recording
      mediaRecorder.start();
      setIsListening(true);
      toast.info('Listening... Click mic again when done speaking', { duration: 3000 });
      console.log('Recording started');
      
    } catch (error) {
      console.error('Error starting recording:', error);
      setIsListening(false);
      toast.error('Could not start recording. Please try again.');
    }
  };
  
  const stopListening = () => {
    console.log('stopListening called');
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsListening(false);
  };

  const handleUserInput = async (text) => {
    if (!text.trim()) return;
    
    setIsListening(false);
    setIsProcessing(true);
    
    // Add to history
    const newHistory = [...conversationHistory, { role: 'user', content: text }];
    setConversationHistory(newHistory);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/demo/voice-chat`, {
        industry: industry,
        message: text,
        conversation_history: newHistory.slice(-6),
        company_name: companyName
      });
      
      const aiText = response.data.response;
      setAiResponse(aiText);
      setConversationHistory([...newHistory, { role: 'assistant', content: aiText }]);
      
      // Check if appointment was scheduled
      if (response.data.appointment) {
        setScheduledAppointment(response.data.appointment);
      }
      
      speakText(aiText);
    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorMsg = "I'm sorry, I'm having trouble processing that. Could you please repeat?";
      setAiResponse(errorMsg);
      speakText(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  const speakText = async (text) => {
    setIsSpeaking(true);
    
    try {
      // Use OpenAI TTS for natural voice
      const response = await axios.post(
        `${BACKEND_URL}/api/demo/text-to-speech`,
        { text, voice: 'nova' },
        { responseType: 'blob' }
      );
      
      // Create audio element and play
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        console.log('TTS audio ended');
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        // Auto-start listening after AI finishes speaking
        if (callActive && !isProcessing) {
          setTimeout(async () => {
            if (callActive) await startListening();
          }, 500);
        }
      };
      
      audio.onerror = (e) => {
        console.error('Audio playback error:', e);
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        toast.error('Voice playback error');
      };
      
      await audio.play();
      
    } catch (error) {
      console.error('TTS error:', error);
      setIsSpeaking(false);
      // Fallback to browser speech synthesis if TTS fails
      fallbackSpeak(text);
    }
  };
  
  // Fallback to browser speech synthesis
  const fallbackSpeak = (text) => {
    synthRef.current.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    utterance.lang = 'en-US';
    
    const voices = synthRef.current.getVoices();
    if (voices.length > 0) {
      const preferredVoice = voices.find(v => 
        v.name.includes('Samantha') || 
        v.name.includes('Google US English') ||
        (v.lang === 'en-US')
      ) || voices[0];
      if (preferredVoice) utterance.voice = preferredVoice;
    }
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      if (callActive && !isProcessing) {
        setTimeout(async () => { if (callActive) await startListening(); }, 500);
      }
    };
    utterance.onerror = () => setIsSpeaking(false);
    
    synthRef.current.speak(utterance);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/')}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                <Phone className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg">Voice Demo</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {/* Industry Badge */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${info.color} mb-6`}>
            <span className="text-xl">{info.icon}</span>
            <span className="font-medium">{info.name}</span>
          </div>

          {!demoStarted ? (
            // Setup Screen - Enter Company Name
            <div>
              <h1 className="text-3xl font-bold mb-4">Try Our AI Voice Assistant</h1>
              <p className="text-slate-400 mb-8">
                Enter your company name to experience how our AI would answer calls for YOUR business.
              </p>
              
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 max-w-md mx-auto">
                <label className="block text-left text-sm text-slate-400 mb-2">Your Company Name</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder={`e.g., Smith's ${info.name}`}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-sky-500 mb-6"
                  data-testid="company-name-input"
                />
                
                <Button
                  onClick={() => setDemoStarted(true)}
                  disabled={!companyName.trim()}
                  className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="start-demo-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Start Voice Demo
                </Button>
                
                <p className="text-slate-500 text-xs mt-4">
                  This demo shows how our AI would handle calls for your {info.name.toLowerCase()} business.
                </p>
              </div>
            </div>
          ) : (
            // Demo Active
            <div>
              <h1 className="text-3xl font-bold mb-2">AI Voice Demo</h1>
              <p className="text-slate-400 mb-2">Simulating calls for:</p>
              <p className="text-xl font-semibold text-sky-400 mb-6">{companyName}</p>

              {/* Permission Warning */}
              {hasPermission === false && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm text-left">
                    Microphone access is required for voice demo. Please allow access in your browser settings and refresh the page.
                  </p>
                </div>
              )}

              {/* Call Interface */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8">
                {!callActive ? (
                  // Start Call Button
                  <div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={startCall}
                      className="w-24 h-24 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/20"
                    >
                      <PhoneCall className="w-10 h-10" />
                </motion.button>
                <p className="text-slate-400">Click to start demo call</p>
              </div>
            ) : (
              // Active Call Interface
              <div>
                {/* Status Indicator */}
                <div className="flex items-center justify-center gap-2 mb-6">
                  <div className={`w-3 h-3 rounded-full ${
                    isSpeaking ? 'bg-blue-400 animate-pulse' :
                    isListening ? 'bg-green-400 animate-pulse' :
                    isProcessing ? 'bg-yellow-400 animate-pulse' :
                    'bg-slate-600'
                  }`} />
                  <span className="text-sm text-slate-400">
                    {isSpeaking ? 'AI Speaking...' :
                     isListening ? 'Listening...' :
                     isProcessing ? 'Processing...' :
                     'Ready - Click mic to speak'}
                  </span>
                </div>

                {/* Manual Listen Button with Tooltip */}
                <div className="relative inline-block">
                  {/* Tooltip Bubble - shows when recording */}
                  {isListening && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute -top-16 left-1/2 -translate-x-1/2 whitespace-nowrap z-10"
                    >
                      <div className="bg-white text-slate-900 px-4 py-2 rounded-lg shadow-lg font-medium text-sm">
                        👆 Click when done speaking!
                      </div>
                      <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white"></div>
                    </motion.div>
                  )}
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={isListening ? stopListening : startListening}
                    disabled={isSpeaking || isProcessing}
                    className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg transition-colors ${
                      isListening 
                        ? 'bg-red-500 shadow-red-500/30 hover:bg-red-600 animate-pulse' 
                        : isSpeaking || isProcessing
                          ? 'bg-slate-700 cursor-not-allowed'
                          : 'bg-gradient-to-br from-sky-500 to-blue-600 shadow-sky-500/30 hover:from-sky-400 hover:to-blue-500'
                    }`}
                  >
                    {isListening ? (
                      <MicOff className="w-8 h-8 text-white" />
                    ) : isProcessing ? (
                      <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                    ) : (
                      <Mic className="w-8 h-8 text-white" />
                    )}
                  </motion.button>
                </div>
                <p className={`text-sm mb-4 font-medium ${isListening ? 'text-red-400' : 'text-slate-500'}`}>
                  {isListening ? '🔴 Recording... Click mic to STOP and send' : isSpeaking ? 'AI is responding...' : 'Tap mic to speak'}
                </p>

                {/* Visualizer */}
                <div className="flex items-center justify-center gap-1 h-16 mb-6">
                  {[...Array(12)].map((_, i) => (
                    <motion.div
                      key={i}
                      className={`w-2 rounded-full ${
                        isListening ? 'bg-red-400' : isSpeaking ? 'bg-sky-400' : 'bg-slate-700'
                      }`}
                      animate={{
                        height: isListening || isSpeaking 
                          ? [16, Math.random() * 48 + 16, 16]
                          : 16
                      }}
                      style={{
                        backgroundColor: isListening ? '#f87171' : (isSpeaking ? '#38bdf8' : '#334155')
                      }}
                      transition={{
                        duration: 0.3,
                        repeat: Infinity,
                        delay: i * 0.05
                      }}
                    />
                  ))}
                </div>

                {/* Last Response */}
                {aiResponse && (
                  <div className="mb-6 p-4 bg-slate-800/50 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Volume2 className="w-5 h-5 text-sky-400 flex-shrink-0 mt-0.5" />
                      <p className="text-slate-300 text-left text-sm">{aiResponse}</p>
                    </div>
                  </div>
                )}

                {/* Scheduled Appointment Card */}
                <AnimatePresence>
                  {scheduledAppointment && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="mb-6"
                    >
                      <div className="p-4 rounded-xl border border-green-500/30 bg-gradient-to-r from-green-500/10 to-emerald-500/10">
                        <div className="flex items-center gap-2 text-green-400 mb-3">
                          <Calendar className="w-5 h-5" />
                          <span className="font-semibold">Appointment Scheduled!</span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-slate-300">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span>{scheduledAppointment.date} at {scheduledAppointment.time}</span>
                          </div>
                          {scheduledAppointment.service && (
                            <p className="text-slate-400">Service: {scheduledAppointment.service}</p>
                          )}
                          <p className="text-xs text-slate-500 mt-2 italic">
                            (This is a demo - no real appointment was created)
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Transcript */}
                {transcript && (
                  <div className="mb-6 p-4 bg-slate-800/50 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Mic className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <p className="text-slate-300 text-left text-sm">You: {transcript}</p>
                    </div>
                  </div>
                )}

                {/* End Call Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={endCall}
                  className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center mx-auto shadow-lg shadow-red-500/20"
                >
                  <PhoneOff className="w-7 h-7" />
                </motion.button>
                <p className="text-slate-500 text-sm mt-3">End Call</p>
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="mt-8 text-left">
            <h3 className="text-sm font-medium text-slate-400 mb-3">Try saying:</h3>
            <div className="space-y-2">
              <p className="text-sm text-slate-500">• "I have an emergency! My {industry === 'hvac' ? 'furnace stopped working' : industry === 'plumbing' ? 'pipe is leaking' : 'system is down'}"</p>
              <p className="text-sm text-slate-500">• "I'd like to schedule an appointment for next week"</p>
              <p className="text-sm text-slate-500">• "What times do you have available tomorrow?"</p>
              <p className="text-sm text-slate-500">• "How much do you charge for a service call?"</p>
            </div>
          </div>

          {/* What This Demo Shows */}
          <div className="mt-6 p-4 bg-slate-800/30 rounded-xl border border-slate-700/50">
            <h3 className="text-sm font-medium text-sky-400 mb-2">What this demo shows:</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              This AI acts as YOUR receptionist, answering calls in your company's name. 
              It can handle inquiries, assess urgency, and schedule appointments using 
              mock availability (simulating a calendar integration like Calendly).
            </p>
          </div>

          {/* Disclaimer */}
          <p className="text-xs text-slate-600 mt-6">
            This is a demo. No actual appointments will be scheduled. 
            Voice recognition works best in Chrome.
          </p>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
